import {Box, Heading, merge, type SxProp} from '@primer/react'
import type {ReactElement, ReactNode, Suspense} from 'react'
import {isValidElement, useCallback, useEffect} from 'react'
import {testIdProps} from '@github-ui/test-id-props'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {gapFromDensity} from '@github-ui/action-bar'

import {ActionBar, type ActionBarProps} from './ActionBar'
import {SelectAllCheckbox} from './SelectAllCheckbox'
import {useListViewSelection} from './SelectionContext'
import {SectionFilters, type SectionFilterLinks} from './SectionFilters'
import {useListViewId} from './IdContext'
import {useNextHeaderTag} from '../hooks/use-next-header-tag'
import {useListViewTitle} from './TitleContext'

export type MetadataProps = {
  /**
   * The rendered title of the list or information regarding its contents.
   */
  title?: string | ReactNode
  /**
   * Links for applying any categorical, mutually exclusive section filters for the content of the ListView.
   */
  sectionFilters?: SectionFilterLinks | ReactElement<typeof Suspense>
  /**
   * Used for assistive announcements that are reflective of the visual updates to the list container a sighted user
   * will experience when using the list. Defaults to no announcement. Visually hidden, for screenreaders only.
   */
  assistiveAnnouncement?: string
  /**
   * Callback for when the checkbox for selecting and deselecting all list items is toggled.
   */
  onToggleSelectAll?: (isSelectAllChecked: boolean) => void
  /**
   * Styling for the actions container.
   */
  actionsSx?: SxProp['sx']
  /**
   * Optional additional actions to show. Will not fall into an overflow dropdown menu to accommodate small screen
   * widths.
   */
  children?: ReactNode
} & SxProp &
  ActionBarProps

const headerStyles: BetterSystemStyleObject = {
  display: 'flex',
  px: 2,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: 1,
}

const defaultStyles: BetterSystemStyleObject = {
  p: 2,
  backgroundColor: 'canvas.subtle',
  borderTopRightRadius: 2,
  borderTopLeftRadius: 2,
  height: 48,
  borderBottom: '1px solid',
  borderBottomColor: 'border.muted',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
}

export const Metadata = ({
  title,
  assistiveAnnouncement,
  sectionFilters,
  children,
  sx,
  actionsSx,
  onToggleSelectAll,
  ...actionBarProps
}: MetadataProps) => {
  const {idPrefix} = useListViewId()
  const {anyItemsSelected} = useListViewSelection()
  const {setHasMetadataTitle} = useListViewTitle()
  const titleTag = useNextHeaderTag('listview-metadata')
  const containerStyles = sx ? merge<BetterSystemStyleObject>(defaultStyles, sx) : defaultStyles

  useEffect(() => {
    setHasMetadataTitle(!!title)
  })

  const ListViewAnnouncements = () => {
    return (
      <div
        id={`${idPrefix}-listview-announcements`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        {...testIdProps('listview-announcement-container')}
      >
        {assistiveAnnouncement}
      </div>
    )
  }

  const ListFilters = useCallback(() => {
    if (!sectionFilters) return null

    if (Array.isArray(sectionFilters)) {
      return <SectionFilters links={sectionFilters} />
    } else if (isValidElement(sectionFilters)) {
      return <>{sectionFilters}</>
    }
    return null
  }, [sectionFilters])

  return (
    <Box
      id={`${idPrefix}-listview-metadata`}
      sx={merge<BetterSystemStyleObject>(
        {gap: anyItemsSelected ? gapFromDensity('spacious') : undefined},
        containerStyles,
      )}
      {...testIdProps('listview-metadata')}
    >
      <SelectAllCheckbox
        onToggle={(isSelectAllChecked: boolean) => {
          if (onToggleSelectAll) onToggleSelectAll(isSelectAllChecked)
        }}
      />

      <ListFilters />

      {title && !anyItemsSelected && (
        <Heading as={titleTag} sx={headerStyles} {...testIdProps('list-view-header-title')}>
          {title}
        </Heading>
      )}

      <ActionBar sx={actionsSx} {...actionBarProps}>
        {children}
      </ActionBar>

      {assistiveAnnouncement && <ListViewAnnouncements />}
    </Box>
  )
}

Metadata.displayName = 'ListView.Metadata'

try{ ListViewAnnouncements.displayName ||= 'ListViewAnnouncements' } catch {}
try{ ListFilters.displayName ||= 'ListFilters' } catch {}